import { useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Flex,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { FiDownload } from "react-icons/fi";
import { useConditionalBackground } from "@/contexts/useConditionalBackground";
import {
  EDGE_COLUMN_PX,
  tableCellStylesThin,
  tableHeaderStylesThin,
  tagButtonStyle,
} from "@/components/FinancialShared/tableStyles";
import { BrlCurrencyFormatter } from "@/utils/BrlCurrencyFormatter";
import { formatDateWithNoHours } from "@/utils/formatDateWithNoHours";
import {
  boletoEstaAtivo,
  boletoEstaExpirado,
  boletoEstaPago,
  IBoletoDetalhadoRetorno,
  IGerarBoletosCobrancaRetorno,
  IParcelaDetalhadaRetorno,
  parcelaQuitada,
  parcelaVencida,
} from "@/models/consignado/proposal-billings.model";
import { renderCompetenciaAAAAMM } from "./renderMesCompetencia";
import GerarBoletoModal from "./GerarBoletoModal";
import GerenciarBoletoModal from "./GerenciarBoletoModal";
import RelatorioQuitacaoModal from "./RelatorioQuitacaoModal";
import { SituacaoParcelasModal } from "./ParcelasRelatoriosModals";

type Props = {
  // CCB do resumo — relatórios e boletos são por CCB.
  numeroCcb: string;
  parcelas: IParcelaDetalhadaRetorno[];
  // Seleção de parcelas (por número) pra "Gerar boletos" — contrato da BMP
  // (NroParcelas: number[]): um pedido cobre várias parcelas.
  parcelasSelecionadas: number[];
  onToggleParcela: (numero: number) => void;
  // Geração deu certo (TAR-045). O boleto só aparece em `boletos[]` após o sync.
  onBoletosGerados: (retorno: IGerarBoletosCobrancaRetorno) => void;
  // Parcelas cuja cobrança foi solicitada e ainda não voltou no resumo
  // (janela de `disponivel_em_segundos`): mostra "em processamento" no lugar
  // do checkbox pra não gerar duas vezes.
  parcelasAguardandoBoleto?: number[];
  // Rejeita em erro (o chamador já mostrou o toast); resolve → fecha o modal.
  onCancelarBoleto: (boleto: IBoletoDetalhadoRetorno) => Promise<unknown>;
  isCancelandoBoleto?: boolean;
};

// A legenda do código `situacao` da BMP não é documentada, então o rótulo sai
// do saldo e do vencimento (parcelaQuitada/parcelaVencida) — a mesma regra dos
// PDFs do backend. O código cru fica no tooltip, pra conferência.
function SituacaoBadge({ parcela }: { parcela: IParcelaDetalhadaRetorno }) {
  const quitada = parcelaQuitada(parcela);
  const { rotulo, esquema } = quitada
    ? { rotulo: "Paga", esquema: "green" }
    : parcelaVencida(parcela)
      ? { rotulo: "Vencida", esquema: "red" }
      : { rotulo: "A vencer", esquema: "gray" };

  return (
    <Tooltip
      label={parcela.situacao != null ? `Código BMP: ${parcela.situacao}` : "Sem código da BMP"}
      placement="top"
    >
      <Badge colorScheme={esquema} borderRadius="md" px={2} py={1} fontSize="10px" fontWeight="normal">
        {rotulo}
      </Badge>
    </Tooltip>
  );
}

const renderDataOuTraco = (data?: string | null) => (data ? formatDateWithNoHours(data) : "—");

// Colunas decimal do pg podem chegar como string — o formatter já aceita.
const renderValorOuTraco = (valor?: number | string | null) =>
  valor != null ? BrlCurrencyFormatter(valor) : "—";

// null = a conciliação ainda não avaliou aquela competência → travessão.
function DivergenciaBadge({ divergente }: { divergente: boolean | null }) {
  if (divergente == null) return <>—</>;
  return (
    <Badge
      colorScheme={divergente ? "red" : "gray"}
      borderRadius="md"
      px={2}
      py={1}
      fontSize="10px"
      fontWeight="normal"
    >
      {divergente ? "Sim" : "Não"}
    </Badge>
  );
}

// Boleto existente: botão que abre o modal "Gerenciar Boleto". Rótulo é o número
// do boleto quando houver, senão o código da cobrança (= codigo_liquidacao da
// geração) encurtado. Pago e cancelado ganham badge no lugar do ícone de download.
const encurtar = (codigo: string) => (codigo.length > 12 ? `${codigo.slice(0, 8)}…` : codigo);

function BoletoButton({
  boleto,
  onClick,
}: {
  boleto: IBoletoDetalhadoRetorno;
  onClick: () => void;
}) {
  const rotulo = boleto.numeroBoleto ?? (boleto.codigoCobranca ? encurtar(boleto.codigoCobranca) : "Boleto");
  const pago = boletoEstaPago(boleto);
  const expirado = !pago && !boleto.cancelado && boletoEstaExpirado(boleto);
  const botao = (
    <Button
      display="inline-flex"
      alignItems="center"
      gap={1}
      color={boleto.cancelado ? "gray.500" : "blue.600"}
      fontFamily="Lexend Deca"
      fontSize="xs"
      fontWeight="normal"
      background="none"
      textDecoration={boleto.cancelado ? "line-through" : undefined}
      _hover={{ textDecoration: boleto.cancelado ? "line-through" : "underline" }}
      onClick={onClick}
      p={0}
      h="auto"
    >
      {!boleto.cancelado && !pago && <Icon as={FiDownload} boxSize={3} />}
      {rotulo}
      {boleto.cancelado ? (
        <Badge colorScheme="gray" borderRadius="md" fontSize="9px" ml={1}>
          Cancelado
        </Badge>
      ) : pago ? (
        <Badge colorScheme="green" borderRadius="md" fontSize="9px" ml={1}>
          Pago
        </Badge>
      ) : expirado ? (
        <Badge colorScheme="orange" borderRadius="md" fontSize="9px" ml={1}>
          Expirado
        </Badge>
      ) : (
        boleto.qrCodeEmv && (
          <Badge colorScheme="teal" borderRadius="md" fontSize="9px" ml={1}>
            PIX
          </Badge>
        )
      )}
    </Button>
  );
  return boleto.linhaDigitavel && !boleto.cancelado ? (
    <Tooltip label={boleto.linhaDigitavel} placement="top">
      {botao}
    </Tooltip>
  ) : (
    botao
  );
}

// Tabela de parcelas. Leitura REAL: `parcelas` de
// GET /proposta/cobrancas/:ccb/resumo-financeiro (TAR-ADM-cobranca-038) — os
// campos planos são as colunas; escrituração/repasse/divergência já vêm casados
// por competência PELO BACKEND. O front não compara nem soma nada.
// Ações:
//  - "Relatório situação das parcelas": REAL (TAR-ADM-cobranca-041) — modal com
//    período + exportar PDF (SituacaoParcelasModal).
//  - "Gerar boletos (N selecionadas)" + checkbox por parcela sem boleto ativo:
//    REAL (TAR-045) — abre o GerarBoletoModal com os overrides do DTO. Ativo é
//    o que o backend também considera ativo (boletoEstaAtivo): cancelado, pago
//    e expirado não contam, e a parcela volta a poder gerar.
//  - modal Gerenciar Boleto: cancelar + download REAIS (TAR-045/046); pago e
//    cancelado escondem as ações.
//  - "Relatório de quitação": REAL (TAR-043/044) — modal com parcelas, data de
//    cálculo e descapitalização + exportar PDF (RelatorioQuitacaoModal).
export default function ParcelasTable({
  numeroCcb,
  parcelas,
  parcelasSelecionadas,
  onToggleParcela,
  onBoletosGerados,
  parcelasAguardandoBoleto = [],
  onCancelarBoleto,
  isCancelandoBoleto = false,
}: Props) {
  const backgroundStyle = useConditionalBackground();
  const boletoModal = useDisclosure();
  const gerarModal = useDisclosure();
  const quitacaoModal = useDisclosure();
  const situacaoModal = useDisclosure();
  const [boletoSelecionado, setBoletoSelecionado] = useState<IBoletoDetalhadoRetorno | null>(null);

  const abrirBoleto = (boleto: IBoletoDetalhadoRetorno) => {
    setBoletoSelecionado(boleto);
    boletoModal.onOpen();
  };

  const qtdSelecionadas = parcelasSelecionadas.length;
  const parcelasParaGerar = parcelas.filter(
    (p) => p.numero_parcela != null && parcelasSelecionadas.includes(p.numero_parcela)
  );

  return (
    <>
      <Flex alignItems="center" justifyContent="flex-end" gap={2} mb={4}>
        <Button
          size="sm"
          {...tagButtonStyle("gray")}
          fontFamily="Lexend Deca"
          fontWeight="normal"
          onClick={situacaoModal.onOpen}
        >
          Relatório situação das parcelas
        </Button>
        <Button
          size="sm"
          {...tagButtonStyle("gray")}
          fontFamily="Lexend Deca"
          fontWeight="normal"
          onClick={quitacaoModal.onOpen}
        >
          Relatório de quitação
        </Button>
        <Button
          size="sm"
          {...tagButtonStyle("green")}
          fontFamily="Lexend Deca"
          fontWeight="normal"
          isDisabled={qtdSelecionadas === 0}
          onClick={gerarModal.onOpen}
        >
          Gerar {qtdSelecionadas === 1 ? "boleto" : "boletos"} ({qtdSelecionadas} selecionada
          {qtdSelecionadas === 1 ? "" : "s"})
        </Button>
      </Flex>

      <TableContainer borderRadius="lg" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead {...backgroundStyle}>
            <Tr>
              <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>Parcela</Th>
              <Th {...tableHeaderStylesThin}>Competência</Th>
              <Th {...tableHeaderStylesThin}>Vencimento</Th>
              <Th {...tableHeaderStylesThin}>Valor</Th>
              <Th {...tableHeaderStylesThin}>Situação</Th>
              <Th {...tableHeaderStylesThin}>Saldo atual</Th>
              <Th {...tableHeaderStylesThin}>Total pago</Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Data<br />escrituração
              </Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Valor<br />escriturado
              </Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Divergência<br />escrituração
              </Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Data<br />repasse
              </Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Valor<br />repassado
              </Th>
              <Th {...tableHeaderStylesThin} whiteSpace="normal">
                Divergência<br />repasse
              </Th>
              <Th {...tableHeaderStylesThin} px={EDGE_COLUMN_PX}>Cobrança</Th>
            </Tr>
          </Thead>
          <Tbody>
            {parcelas.length === 0 && (
              <Tr>
                <Td {...tableCellStylesThin} colSpan={14} textAlign="center" color="gray.500">
                  Nenhuma parcela encontrada para esta CCB.
                </Td>
              </Tr>
            )}
            {parcelas.map((parcela) => {
              const numero = parcela.numero_parcela;
              return (
                <Tr key={parcela.id} _odd={{ bg: "blackAlpha.50" }}>
                  <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>{numero ?? "—"}</Td>
                  <Td {...tableCellStylesThin}>
                    {parcela.competencia != null ? renderCompetenciaAAAAMM(parcela.competencia) : "—"}
                  </Td>
                  <Td {...tableCellStylesThin}>{renderDataOuTraco(parcela.vencimento)}</Td>
                  <Td {...tableCellStylesThin}>{BrlCurrencyFormatter(parcela.valor)}</Td>
                  <Td {...tableCellStylesThin}><SituacaoBadge parcela={parcela} /></Td>
                  <Td {...tableCellStylesThin}>{BrlCurrencyFormatter(parcela.saldo_atual)}</Td>
                  <Td {...tableCellStylesThin}>{BrlCurrencyFormatter(parcela.total_pago)}</Td>
                  <Td {...tableCellStylesThin}>{renderDataOuTraco(parcela.data_escrituracao)}</Td>
                  <Td {...tableCellStylesThin}>{renderValorOuTraco(parcela.valor_escriturado)}</Td>
                  <Td {...tableCellStylesThin}>
                    <DivergenciaBadge divergente={parcela.divergencia_escrituracao} />
                  </Td>
                  <Td {...tableCellStylesThin}>{renderDataOuTraco(parcela.data_repasse)}</Td>
                  <Td {...tableCellStylesThin}>{renderValorOuTraco(parcela.valor_repassado)}</Td>
                  <Td {...tableCellStylesThin}>
                    <DivergenciaBadge divergente={parcela.divergencia_repasse} />
                  </Td>
                  <Td {...tableCellStylesThin} px={EDGE_COLUMN_PX}>
                    {parcela.boletos.some(boletoEstaAtivo) ? (
                      <Flex direction="column" gap={1} align="center">
                        {parcela.boletos.map((boleto) => (
                          <BoletoButton key={boleto.id} boleto={boleto} onClick={() => abrirBoleto(boleto)} />
                        ))}
                      </Flex>
                    ) : numero != null && parcelasAguardandoBoleto.includes(numero) ? (
                      <Tooltip
                        label="Cobrança solicitada à bancarizadora. O boleto aparece aqui após a sincronização."
                        placement="top"
                      >
                        <Badge colorScheme="yellow" borderRadius="md" px={2} py={1} fontSize="10px" fontWeight="normal">
                          Boleto em processamento
                        </Badge>
                      </Tooltip>
                    ) : parcelaQuitada(parcela) ? (
                      parcela.boletos.length ? (
                        <Flex direction="column" gap={1} align="center">
                          {parcela.boletos.map((boleto) => (
                            <BoletoButton key={boleto.id} boleto={boleto} onClick={() => abrirBoleto(boleto)} />
                          ))}
                        </Flex>
                      ) : (
                        "—"
                      )
                    ) : numero != null ? (
                      <Flex direction="column" gap={1} align="center">
                        {parcela.boletos.map((boleto) => (
                          <BoletoButton key={boleto.id} boleto={boleto} onClick={() => abrirBoleto(boleto)} />
                        ))}
                        <Checkbox
                          colorScheme="green"
                          isChecked={parcelasSelecionadas.includes(numero)}
                          onChange={() => onToggleParcela(numero)}
                          fontFamily="Lexend Deca"
                          fontSize="xs"
                          sx={{ ".chakra-checkbox__control": { borderColor: "gray.500" } }}
                        >
                          Gerar boleto
                        </Checkbox>
                      </Flex>
                    ) : (
                      "—"
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>

      <GerarBoletoModal
        numeroCcb={numeroCcb}
        parcelas={parcelasParaGerar}
        isOpen={gerarModal.isOpen}
        onClose={gerarModal.onClose}
        onSucesso={onBoletosGerados}
      />
      <GerenciarBoletoModal
        boleto={boletoSelecionado}
        isOpen={boletoModal.isOpen}
        onClose={boletoModal.onClose}
        isCancelando={isCancelandoBoleto}
        onCancelar={async (boleto) => {
          try {
            await onCancelarBoleto(boleto);
            boletoModal.onClose();
          } catch {
            // Toast já emitido por quem cancela; modal fica aberto.
          }
        }}
      />
      <RelatorioQuitacaoModal
        isOpen={quitacaoModal.isOpen}
        onClose={quitacaoModal.onClose}
        numeroCcb={numeroCcb}
        parcelas={parcelas}
      />
      <SituacaoParcelasModal
        isOpen={situacaoModal.isOpen}
        onClose={situacaoModal.onClose}
        numeroCcb={numeroCcb}
      />
    </>
  );
}
